<template>
    <div class="export-job-status-cell">
        <template v-if="isFailed">
            <b-badge pill variant="danger" class="export-job-status-cell__badge">
                {{ labelFailed }}
            </b-badge>
        </template>
        <template v-else-if="isCompleted">
            <b-badge pill variant="success" class="export-job-status-cell__badge">
                {{ labelCompleted }}
            </b-badge>
        </template>
        <template v-else-if="isExporting">
            <div
                class="export-job-status-cell__progress"
                role="status"
                :aria-label="ariaExporting"
            >
                <div class="export-job-status-cell__progress-top">
                    <span class="export-job-status-cell__exporting-label">{{ labelExporting }}</span>
                    <span class="export-job-status-cell__percent">{{ progressValue }}%</span>
                </div>
                <div class="progress export-job-status-cell__track">
                    <b-progress-bar
                        :value="progressValue"
                        :max="100"
                        variant="primary"
                        class="progress-pdv export-job-status-cell__bar"
                    />
                </div>
            </div>
        </template>
        <template v-else-if="status">
            <b-badge pill :variant="idleBadgeVariant" class="export-job-status-cell__badge">
                {{ idleStatusLabel }}
            </b-badge>
        </template>
    </div>
</template>

<script>
import { isExportJobTimedOut } from '@src/utils/exportJobTimeout'

const i18nKey = 'EXPORT_JOBS'

export default {
    name: 'ExportJobStatusCell',
    props: {
        row: {
            type: Object,
            default: null,
        },
    },
    computed: {
        status() {
            return this.row && this.row.status ? this.row.status : null
        },
        progressValue() {
            const n = Number(this.row && this.row.progress)
            return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : 0
        },
        inFlight() {
            return ['pending', 'started', 'scheduled'].includes(this.status)
        },
        isTimeoutFailure() {
            return (
                (this.row && this.row.failureReason === 'timeout') ||
                isExportJobTimedOut(this.row)
            )
        },
        isFailed() {
            return this.status === 'failed' || this.isTimeoutFailure
        },
        isCompleted() {
            return this.status === 'completed'
        },
        isExporting() {
            return (
                this.inFlight &&
                this.progressValue > 0 &&
                !this.isTimeoutFailure
            )
        },
        labelFailed() {
            if (this.isTimeoutFailure) {
                return this.getI18n(i18nKey, 'STATUSES.failed_timeout')
            }
            return this.getI18n(i18nKey, 'STATUSES.failed')
        },
        labelCompleted() {
            return this.getI18n(i18nKey, 'STATUSES.completed')
        },
        labelExporting() {
            return this.getI18n(i18nKey, 'MESSAGES.exporting')
        },
        idleStatusLabel() {
            if (!this.status) {
                return ''
            }
            return this.getI18n(i18nKey, `STATUSES.${this.status}`)
        },
        idleBadgeVariant() {
            if (this.status === 'pending') {
                return 'warning'
            }
            if (this.status === 'started') {
                return 'info'
            }
            if (this.status === 'scheduled') {
                return 'secondary'
            }
            return 'secondary'
        },
        ariaExporting() {
            return `${this.labelExporting} ${this.progressValue}%`
        },
    },
}
</script>

<style lang="scss" scoped>
.export-job-status-cell {
    min-width: 8rem;
    max-width: 11rem;
    font-size: inherit;
    line-height: inherit;
    color: inherit;
}

.export-job-status-cell__badge {
    padding: 0.1rem 0.4rem;
    font-size: inherit;
    font-weight: normal;
    line-height: 1;
    vertical-align: baseline;
}

.export-job-status-cell__progress {
    padding: 0;
    margin: 0;
}

.export-job-status-cell__progress-top {
    display: flex;
    gap: 0.35rem;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 0.2rem;
    font-size: inherit;
    font-weight: normal;
    line-height: inherit;
    color: inherit;
}

.export-job-status-cell__exporting-label,
.export-job-status-cell__percent {
    font-size: inherit;
    font-weight: normal;
    line-height: inherit;
    color: inherit;
}

.export-job-status-cell__percent {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
}

.export-job-status-cell__track {
    height: 4px;
    overflow: hidden;
    background-color: #e9ecef;
    border-radius: 999px;
}

.export-job-status-cell__bar {
    border-radius: 999px;
}
</style>
